<script>
  import { Link } from '@inertiajs/svelte'
  import ApplicationLayout from '../../components/ApplicationLayout/index.svelte'
  import ContainerTree from '../../components/ContainerTree.svelte'
  import ItemsList from '../../components/ItemsList.svelte'
  import PropertySearch from '../../components/PropertySearch/index.svelte'

  let { property, spaces, property_users, items = [], containers = [], can_manage_spaces, can_manage_users, can_edit_property, can_manage_containers, pagination_meta = {} } = $props()

  // Track container count for display
  let currentContainerCount = $state(containers.length)
  let totalContainerCount = $state(pagination_meta.containers?.total_count || containers.length)

  const totalItemCount = pagination_meta.items?.total_count || items.length

  // Callback for ContainerTree to update counts
  function handleContainerCountUpdate(currentCount, totalCount) {
    currentContainerCount = currentCount
    totalContainerCount = totalCount
  }

  function getRoleBadgeClass(roleName) {
    switch(roleName) {
      case 'owner':
        return 'bg-brand-100 text-brand-800'
      case 'admin':
        return 'bg-brand-100 text-brand-800'
      case 'guest':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  function userInitials(email) {
    return (email || '?')
      .split('@')[0]
      .split(/[._-]/)
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || '?'
  }

  function formatPropertyType(type) {
    if (!type) return 'Not specified'
    return type.toString().split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }
</script>

<svelte:head>
  <title>{property.name} | Maset</title>
</svelte:head>

<ApplicationLayout>
  <!-- Breadcrumb -->
  <nav class="flex items-center gap-2 text-sm text-gray-500 mb-4" aria-label="Breadcrumb">
    <Link href="/properties" class="hover:text-brand-700">Properties</Link>
    <span class="text-gray-400">/</span>
    <span class="font-semibold text-gray-900">{property.name}</span>
  </nav>

  <!-- Property hero: identity, stats and primary actions -->
  <section class="relative rounded-2xl overflow-hidden bg-gradient-to-br from-brand-900 via-brand-700 to-teal-600 shadow-md">
    <div class="absolute inset-0 pointer-events-none" style="background: radial-gradient(600px 200px at 90% -20%, rgba(255,255,255,0.14), transparent 60%), radial-gradient(400px 180px at -10% 120%, rgba(255,255,255,0.08), transparent 60%);"></div>
    <div class="relative px-6 py-6 sm:px-8">
      <div class="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 class="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">{property.name}</h1>
          <div class="flex flex-wrap items-center gap-3 mt-2 text-sm text-white/85">
            <span class="bg-white/20 border border-white/25 px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider">
              {formatPropertyType(property.property_type)}
            </span>
            {#if property.address}
              <span class="inline-flex items-center gap-1.5">
                <svg class="h-4 w-4 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {property.address.full_address}
              </span>
            {/if}
          </div>
        </div>
        <div class="flex gap-2.5">
          <Link
            href={`/properties/${property.id}/pending_images`}
            class="hidden sm:inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-brand-800 shadow-sm hover:bg-brand-50 transition-colors"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
            Quick Capture
          </Link>
          {#if can_edit_property}
            <Link
              href={`/properties/${property.id}/edit`}
              class="inline-flex items-center gap-2 rounded-lg bg-white/15 border border-white/30 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25 transition-colors"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </Link>
          {/if}
        </div>
      </div>

      <!-- At-a-glance stats -->
      <div class="flex flex-wrap gap-2.5 mt-5">
        <div class="bg-white/10 border border-white/20 rounded-lg px-4 py-2 min-w-[6.5rem] flex-1 sm:flex-none">
          <b class="block text-xl font-extrabold text-white">{totalContainerCount}</b>
          <span class="text-[11px] uppercase tracking-wider text-white/75">Containers</span>
        </div>
        <div class="bg-white/10 border border-white/20 rounded-lg px-4 py-2 min-w-[6.5rem] flex-1 sm:flex-none">
          <b class="block text-xl font-extrabold text-white">{totalItemCount}</b>
          <span class="text-[11px] uppercase tracking-wider text-white/75">Items</span>
        </div>
        <div class="bg-white/10 border border-white/20 rounded-lg px-4 py-2 min-w-[6.5rem] flex-1 sm:flex-none">
          <b class="block text-xl font-extrabold text-white">{spaces.length}</b>
          <span class="text-[11px] uppercase tracking-wider text-white/75">Spaces</span>
        </div>
        {#if property.square_meters}
          <div class="bg-white/10 border border-white/20 rounded-lg px-4 py-2 min-w-[6.5rem] flex-1 sm:flex-none">
            <b class="block text-xl font-extrabold text-white">{property.square_meters}</b>
            <span class="text-[11px] uppercase tracking-wider text-white/75">m²</span>
          </div>
        {/if}
      </div>
    </div>
  </section>

  <!-- Global search -->
  <div class="my-5">
    <PropertySearch propertyId={property.id} propertyName={property.name} />
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
    <!-- Main column -->
    <div class="lg:col-span-2 space-y-6">
      <!-- Containers -->
      <section class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div class="flex justify-between items-center gap-3 px-5 py-4 border-b border-gray-100">
          <h2 class="text-base font-bold text-gray-900 flex items-center gap-2">
            Containers
            {#if totalContainerCount > 0}
              <span class="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{totalContainerCount}</span>
            {/if}
          </h2>
          {#if can_manage_containers}
            <a href={`/properties/${property.id}/containers/new`} class="text-sm font-semibold text-brand-700 hover:underline">
              + Add container
            </a>
          {/if}
        </div>

        <div class="p-4">
          {#if containers.length === 0}
            <div class="text-center py-8">
              <div class="text-gray-400 mb-4">
                <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p class="text-gray-500">No containers added yet.</p>
              {#if can_manage_containers}
                <a href={`/properties/${property.id}/containers/new`} class="mt-4 inline-block text-sm font-semibold text-brand-700 hover:underline">
                  + Add your first container
                </a>
              {/if}
            </div>
          {:else}
            <ContainerTree
              containers={containers}
              canManageContainers={can_manage_containers}
              propertyId={property.id}
              paginationMeta={pagination_meta.containers}
              onCountUpdate={handleContainerCountUpdate}
            />
          {/if}
        </div>
      </section>

      <!-- Items -->
      <ItemsList items={items} propertyId={property.id} paginationMeta={pagination_meta.items} />
    </div>

    <!-- Sidebar -->
    <aside class="space-y-4">
      <!-- Details -->
      <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h2 class="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Details</h2>
        <dl class="space-y-3 text-sm">
          {#if property.square_meters}
            <div class="flex justify-between gap-3">
              <dt class="text-gray-500">Size</dt>
              <dd class="font-semibold text-gray-900 text-right">{property.square_meters} m²</dd>
            </div>
          {/if}
          {#if property.year_built}
            <div class="flex justify-between gap-3">
              <dt class="text-gray-500">Year built</dt>
              <dd class="font-semibold text-gray-900 text-right">{property.year_built}</dd>
            </div>
          {/if}
          <div class="flex justify-between gap-3">
            <dt class="text-gray-500">Type</dt>
            <dd class="font-semibold text-gray-900 text-right">{formatPropertyType(property.property_type)}</dd>
          </div>
          {#if property.address}
            <div class="flex justify-between gap-3">
              <dt class="text-gray-500">Address</dt>
              <dd class="font-semibold text-gray-900 text-right whitespace-pre-line">{property.address.full_address}</dd>
            </div>
          {/if}
        </dl>
        {#if property.notes}
          <p class="mt-4 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2.5 whitespace-pre-line">{property.notes}</p>
        {/if}
      </div>

      <!-- Spaces -->
      <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h2 class="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Spaces</h2>
        {#if spaces.length === 0}
          <p class="text-sm text-gray-500 py-2">No spaces added yet.</p>
        {:else}
          <ul class="divide-y divide-gray-100">
            {#each spaces as space}
              <li class="py-2.5 text-sm font-medium text-gray-900">{space.name}</li>
            {/each}
          </ul>
        {/if}
      </div>

      <!-- Members -->
      <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h2 class="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Members</h2>
        {#if property_users.length === 0}
          <p class="text-sm text-gray-500 py-2">No users assigned yet.</p>
        {:else}
          <ul class="divide-y divide-gray-100">
            {#each property_users as property_user}
              <li class="py-2.5 flex items-center gap-2.5">
                <span class="h-8 w-8 flex-shrink-0 rounded-full bg-brand-100 text-brand-800 grid place-items-center text-xs font-bold">
                  {userInitials(property_user.user.email)}
                </span>
                <span class="flex-1 min-w-0 truncate text-sm font-medium text-gray-900">{property_user.user.email}</span>
                <span class={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${getRoleBadgeClass(property_user.role.name)}`}>
                  {property_user.role.name}
                </span>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    </aside>
  </div>

  <!-- Mobile: Quick Capture floating action button -->
  <Link
    href={`/properties/${property.id}/pending_images`}
    class="sm:hidden fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-brand-700 text-white grid place-items-center shadow-lg shadow-brand-900/30"
    aria-label="Quick Capture"
  >
    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
  </Link>
</ApplicationLayout>
